import { Link } from 'react-router-dom';
import { useRef, useState, useEffect } from 'react';

export default function CraftTeaBanner() {
  const sectionRef = useRef<HTMLElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) setIsVisible(true);
      },
      { threshold: 0.15 }
    );

    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      className="relative py-20 sm:py-28 md:py-36 bg-[#FAFAF8] overflow-hidden"
      aria-labelledby="tea-banner-heading"
    >
      <div className="relative max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left: Content */}
          <div
            className={`transition-all duration-1000 ${
              isVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-12"
            }`}
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#2D5F4F]/10 mb-6">
              <i className="ri-leaf-line text-sm text-[#2D5F4F]" aria-hidden="true" />
              <span className="text-xs font-medium tracking-[0.2em] text-[#2D5F4F] uppercase">
                Collaboration
              </span>
            </div>

            <h2
              id="tea-banner-heading"
              className="text-3xl sm:text-4xl md:text-5xl font-extralight tracking-[0.08em] text-neutral-900 mb-5"
            >
              Craft Tea × MeVER
            </h2>

            <p className="text-base sm:text-lg font-light leading-relaxed text-neutral-600 mb-8">
              クリエイティブな時間に、本物の味わいを。
              <br />
              Craft Tea厳選のシングルオリジン日本茶・和紅茶を、
              <br className="hidden sm:inline" />
              あなたのワークスペースへ。
            </p>

            <div className="space-y-4 mb-10">
              {[
                { icon: "ri-plant-line", text: "シングルオリジンの厳選茶葉" },
                { icon: "ri-calendar-line", text: "月替わりで新しい発見" },
                { icon: "ri-truck-line", text: "ポスト投函で受け取り簡単" },
              ].map((item, index) => (
                <div
                  key={index}
                  className={`flex items-center gap-3 transition-all duration-700 ${
                    isVisible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-8"
                  }`}
                  style={{ transitionDelay: `${(index + 1) * 150}ms` }}
                >
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#2D5F4F]/10 shrink-0">
                    <i className={`${item.icon} text-lg text-[#2D5F4F]`} aria-hidden="true" />
                  </div>
                  <p className="text-sm font-light text-neutral-700">{item.text}</p>
                </div>
              ))}
            </div>

            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/tea"
                className="inline-flex items-center justify-center gap-2 px-8 py-3.5 bg-[#2D5F4F] text-white text-sm font-normal tracking-[0.08em] hover:bg-[#234739] transition-all whitespace-nowrap rounded-md cursor-pointer"
                aria-label="Craft Teaサブスクリプション詳細ページへ"
              >
                詳しく見る
                <i className="ri-arrow-right-line text-base" aria-hidden="true" />
              </Link>
              <a
                href="#tea-plans"
                onClick={(e) => {
                  e.preventDefault();
                  document.querySelector("#tea-plans")?.scrollIntoView({ behavior: "smooth" });
                }}
                className="inline-flex items-center justify-center gap-2 px-8 py-3.5 bg-white text-[#2D5F4F] text-sm font-light tracking-[0.08em] hover:bg-neutral-50 transition-all whitespace-nowrap rounded-md border border-[#2D5F4F]/20 cursor-pointer"
                aria-label="プラン一覧セクションへスクロール"
              >
                プランを見る
              </a>
            </div>
          </div>

          {/* Right: Image */}
          <div
            className={`relative transition-all duration-1000 delay-200 ${
              isVisible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-12"
            }`}
          >
            <div className="relative aspect-[4/5] rounded-2xl overflow-hidden bg-white shadow-xl">
              <img
                src="https://public.readdy.ai/ai/img_res/fc1f67a73548c4ce16b0c6e428380f41.jpg"
                alt="Craft Tea × MeVER コラボレーション"
                className="w-full h-full object-cover object-center"
                loading="lazy"
                width="800"
                height="1000"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#2D5F4F]/10 via-transparent to-transparent" />
            </div>

            {/* Floating price tag */}
            <div
              className={`absolute -top-6 -right-6 bg-white rounded-2xl shadow-2xl px-6 py-4 transition-all duration-1000 delay-500 ${
                isVisible ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-8"
              }`}
            >
              <p className="text-xs font-medium text-neutral-400 tracking-wider uppercase mb-1">
                From
              </p>
              <p className="text-2xl font-light text-neutral-900 tracking-tight">
                ¥1,620
                <span className="text-sm text-neutral-500 ml-1">/月</span>
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
